import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { NotificationSettings } from '@nexus/core';
import { isTauri } from '@nexus/store/tauri';
import { useStore } from '../db/context';
import { useProjects } from '../db/tasks';
import { loadNotificationSettings, saveNotificationSettings } from './settings';
import { permission, requestPermission, type Permission } from './system';
import { PushSettings } from '../push/PushSettings';
import { desktopPrefs, setAutostart, setCloseToTray } from './desktop';

const field = 'rounded-lg border border-border bg-bg px-2 py-1 text-sm';

export function NotificationSettingsForm() {
  const { t } = useTranslation();
  const store = useStore();
  const projects = useProjects()?.projects ?? [];
  const [s, setS] = useState<NotificationSettings | null>(null);
  const [perm, setPerm] = useState<Permission>('default');
  const [desktop, setDesktop] = useState<{
    closeToTray: boolean;
    autostart: boolean | null;
  } | null>(null);

  useEffect(() => {
    void loadNotificationSettings(store).then(setS);
    void permission().then(setPerm);
    if (isTauri()) void desktopPrefs(store).then(setDesktop);
  }, [store]);

  if (!s) return null;
  const update = (patch: Partial<NotificationSettings>) => {
    const next = { ...s, ...patch };
    setS(next);
    void saveNotificationSettings(store, next);
  };
  const row = 'grid grid-cols-[1fr_auto] items-center gap-3';
  const weekdays = Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat(undefined, { weekday: 'long' }).format(new Date(2026, 9, 4 + i)),
  ); // 4 Oct 2026 is a Sunday

  return (
    <div className="flex flex-col gap-3 text-sm">
      <label className={row}>
        {t('notifications.settings.enabled')}
        <input
          type="checkbox"
          checked={s.enabled}
          onChange={(e) => update({ enabled: e.target.checked })}
        />
      </label>
      <div className={row}>
        <label htmlFor="ns-system">{t('notifications.settings.system')}</label>
        <input
          id="ns-system"
          type="checkbox"
          checked={s.system}
          onChange={(e) => update({ system: e.target.checked })}
        />
      </div>
      {s.system && perm !== 'granted' && (
        <div className="rounded-md bg-bg-subtle p-2 text-xs">
          {perm === 'unsupported' ? (
            t('notifications.settings.unsupported')
          ) : perm === 'denied' ? (
            t('notifications.settings.denied')
          ) : (
            <button
              type="button"
              className="text-accent-ink underline"
              onClick={() => void requestPermission().then(setPerm)}
            >
              {t('notifications.settings.allow')}
            </button>
          )}
        </div>
      )}
      {!isTauri() && <PushSettings />}

      <fieldset className="flex flex-col gap-2 rounded-lg border border-border p-2">
        <legend className="px-1 text-xs text-fg-muted">{t('notifications.settings.rules')}</legend>
        <label className={row}>
          <span className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={s.dueRule.enabled}
              onChange={(e) => update({ dueRule: { ...s.dueRule, enabled: e.target.checked } })}
            />
            {t('notifications.settings.dueRule')}
          </span>
          <input
            type="time"
            className={field}
            aria-label={t('notifications.settings.dueRule')}
            value={s.dueRule.time}
            onChange={(e) => update({ dueRule: { ...s.dueRule, time: e.target.value } })}
          />
        </label>
        <label className={row}>
          <span className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={s.highPriorityDayBefore.enabled}
              onChange={(e) =>
                update({
                  highPriorityDayBefore: { ...s.highPriorityDayBefore, enabled: e.target.checked },
                })
              }
            />
            {t('notifications.settings.dayBefore')}
          </span>
          <input
            type="time"
            className={field}
            aria-label={t('notifications.settings.dayBefore')}
            value={s.highPriorityDayBefore.time}
            onChange={(e) =>
              update({
                highPriorityDayBefore: { ...s.highPriorityDayBefore, time: e.target.value },
              })
            }
          />
        </label>
        <label className={row}>
          <span className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={s.dailySummary.enabled}
              onChange={(e) =>
                update({ dailySummary: { ...s.dailySummary, enabled: e.target.checked } })
              }
            />
            {t('notifications.settings.daily')}
          </span>
          <input
            type="time"
            className={field}
            aria-label={t('notifications.settings.daily')}
            value={s.dailySummary.time}
            onChange={(e) => update({ dailySummary: { ...s.dailySummary, time: e.target.value } })}
          />
        </label>
        <div className={row}>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={s.weeklySummary.enabled}
              onChange={(e) =>
                update({ weeklySummary: { ...s.weeklySummary, enabled: e.target.checked } })
              }
            />
            {t('notifications.settings.weekly')}
          </label>
          <span className="flex gap-1">
            <select
              className={field}
              aria-label={t('notifications.settings.weeklyDay')}
              value={s.weeklySummary.weekday}
              onChange={(e) =>
                update({ weeklySummary: { ...s.weeklySummary, weekday: Number(e.target.value) } })
              }
            >
              {weekdays.map((d, i) => (
                <option key={d} value={i}>
                  {d}
                </option>
              ))}
            </select>
            <input
              type="time"
              className={field}
              aria-label={t('notifications.settings.weekly')}
              value={s.weeklySummary.time}
              onChange={(e) =>
                update({ weeklySummary: { ...s.weeklySummary, time: e.target.value } })
              }
            />
          </span>
        </div>
      </fieldset>

      <div className={row}>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={s.quietHours.enabled}
            onChange={(e) => update({ quietHours: { ...s.quietHours, enabled: e.target.checked } })}
          />
          {t('notifications.settings.quiet')}
        </label>
        <span className="flex items-center gap-1">
          <input
            type="time"
            className={field}
            aria-label={t('notifications.settings.quietStart')}
            value={s.quietHours.start}
            onChange={(e) => update({ quietHours: { ...s.quietHours, start: e.target.value } })}
          />
          –
          <input
            type="time"
            className={field}
            aria-label={t('notifications.settings.quietEnd')}
            value={s.quietHours.end}
            onChange={(e) => update({ quietHours: { ...s.quietHours, end: e.target.value } })}
          />
        </span>
      </div>
      <label className={row}>
        {t('notifications.settings.snooze')}
        <select
          className={field}
          value={s.defaultSnoozeMinutes}
          onChange={(e) => update({ defaultSnoozeMinutes: Number(e.target.value) })}
        >
          {[5, 10, 15, 30, 60].map((m) => (
            <option key={m} value={m}>
              {t('notifications.minutes', { count: m })}
            </option>
          ))}
        </select>
      </label>
      <label className={row}>
        {t('notifications.settings.mutedTags')}
        <input
          className={field}
          defaultValue={s.mutedTags.join(', ')}
          placeholder="privé, archief"
          onBlur={(e) =>
            update({
              mutedTags: e.target.value
                .split(',')
                .map((x) => x.trim().replace(/^#/, ''))
                .filter(Boolean),
            })
          }
        />
      </label>
      {projects.length > 0 && (
        <details>
          <summary className="cursor-pointer">
            {t('notifications.settings.mutedProjects', { count: s.mutedProjects.length })}
          </summary>
          <ul className="mt-1 max-h-32 overflow-y-auto">
            {projects.map((p) => (
              <li key={p.page.id}>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={s.mutedProjects.includes(p.page.id)}
                    onChange={(e) =>
                      update({
                        mutedProjects: e.target.checked
                          ? [...s.mutedProjects, p.page.id]
                          : s.mutedProjects.filter((x) => x !== p.page.id),
                      })
                    }
                  />
                  {p.page.title}
                </label>
              </li>
            ))}
          </ul>
        </details>
      )}

      {desktop && (
        <fieldset className="flex flex-col gap-2 rounded-lg border border-border p-2">
          <legend className="px-1 text-xs text-fg-muted">
            {t('notifications.settings.desktop')}
          </legend>
          <label className={row}>
            {t('notifications.settings.closeToTray')}
            <input
              type="checkbox"
              checked={desktop.closeToTray}
              onChange={(e) => {
                const v = e.target.checked;
                setDesktop({ ...desktop, closeToTray: v });
                void setCloseToTray(store, v);
              }}
            />
          </label>
          {desktop.autostart !== null && (
            <label className={row}>
              {t('notifications.settings.autostart')}
              <input
                type="checkbox"
                checked={desktop.autostart}
                onChange={(e) => {
                  const v = e.target.checked;
                  setDesktop({ ...desktop, autostart: v });
                  void setAutostart(v);
                }}
              />
            </label>
          )}
        </fieldset>
      )}
    </div>
  );
}
